import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Filter, Download, X, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsView = () => {
  const [allCheques, setAllCheques] = useState([]);
  const [filteredCheques, setFilteredCheques] = useState([]);
  const [filters, setFilters] = useState({
    customer_name: "",
    payment_method: "all",
    start_date: "",
    end_date: ""
  });
  const [showFilters, setShowFilters] = useState(true);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [editingCheque, setEditingCheque] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllCheques();
    const interval = setInterval(() => {
      fetchAllCheques();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let count = 0;
    if (filters.customer_name) count++;
    if (filters.payment_method !== 'all') count++;
    if (filters.start_date && filters.end_date) count++;
    setActiveFiltersCount(count);
  }, [filters]);

  const fetchAllCheques = async () => {
    try {
      const response = await axios.get(`${API}/cheques`);
      setAllCheques(response.data);
      setFilteredCheques(response.data);
    } catch (error) {
      console.error("Error fetching cheques:", error);
      toast.error("Failed to fetch cheques");
    }
  };

  const applyFilters = async () => {
    try {
      const params = new URLSearchParams();
      
      if (filters.customer_name) {
        params.append('customer_name', filters.customer_name);
      }
      if (filters.payment_method !== 'all') {
        params.append('payment_method', filters.payment_method);
      }
      if (filters.start_date && filters.end_date) {
        params.append('start_date', filters.start_date);
        params.append('end_date', filters.end_date);
      }

      const response = await axios.get(`${API}/cheques?${params.toString()}`);
      setFilteredCheques(response.data);
      toast.success(`Found ${response.data.length} records`);
    } catch (error) {
      console.error("Error applying filters:", error);
      toast.error("Failed to apply filters");
    }
  };

  const clearFilters = () => {
    setFilters({
      customer_name: "",
      payment_method: "all",
      start_date: "",
      end_date: ""
    });
    setFilteredCheques(allCheques);
    toast.info("Filters cleared");
  };

  const exportToCSV = () => {
    if (filteredCheques.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Customer Name", "Cheque Number", "Cheque Amount", "Amount Received", "Payment Method", "Payment Date", "Cheque Return Date", "Returned To"];
    const csvContent = [
      headers.join(","),
      ...filteredCheques.map(cheque => [
        cheque.customer_name,
        cheque.cheque_number,
        cheque.cheque_amount,
        cheque.amount_received,
        cheque.payment_method,
        cheque.payment_date,
        cheque.cheque_return_date,
        cheque.returned_to
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cheque-returns-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Report exported successfully");
  };

  const handleEdit = (cheque) => {
    setEditingCheque({...cheque});
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/cheques/${editingCheque.id}`, {
        ...editingCheque,
        cheque_amount: parseFloat(editingCheque.cheque_amount),
        amount_received: parseFloat(editingCheque.amount_received)
      });

      toast.success("Cheque updated successfully!");
      setShowEditModal(false);
      setEditingCheque(null);
      fetchAllCheques();
    } catch (error) {
      console.error("Error updating cheque:", error);
      toast.error("Failed to update cheque");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/cheques/${id}`);
      toast.success("Cheque deleted successfully!");
      setDeleteConfirm(null);
      fetchAllCheques();
    } catch (error) {
      console.error("Error deleting cheque:", error);
      toast.error("Failed to delete cheque");
    }
  };

  const handleEditChange = (e) => {
    setEditingCheque({
      ...editingCheque,
      [e.target.name]: e.target.value
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-6">
      {/* Filters Section */}
      <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-800/50 rounded-lg">
                <Filter className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
                  Filters
                </h2>
                {activeFiltersCount > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">{activeFiltersCount} active filter{activeFiltersCount > 1 ? 's' : ''}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
                className="lg:hidden px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors text-sm font-medium"
              >
                {showFilters ? 'Hide' : 'Show'}
              </button>
              <button
                onClick={exportToCSV}
                data-testid="export-csv-btn"
                className="px-4 py-2 bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-700 hover:to-slate-600 text-slate-200 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-lg shadow-slate-900/30"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export CSV</span>
                <span className="sm:hidden">CSV</span>
              </button>
            </div>
          </div>
        </div>

        <div className={`${showFilters ? 'block' : 'hidden'} lg:block border-t border-slate-800 p-6 bg-slate-950/30`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                Customer Name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  name="customer_name"
                  data-testid="filter-customer-name-input"
                  value={filters.customer_name}
                  onChange={handleFilterChange}
                  placeholder="Search customer..."
                  className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-slate-300 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                Payment Method
              </label>
              <select
                name="payment_method"
                data-testid="filter-payment-method-select"
                value={filters.payment_method}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Methods</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                data-testid="filter-start-date-input"
                value={filters.start_date}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                data-testid="filter-end-date-input"
                value={filters.end_date}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={applyFilters}
              data-testid="apply-filters-btn"
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/30 hover:shadow-emerald-900/50"
            >
              <Search className="w-4 h-4" />
              Apply Filters
            </button>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters-btn"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
              All Cheque Returns
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {filteredCheques.length} {filteredCheques.length === 1 ? 'record' : 'records'} found
            </p>
          </div>
        </div>

        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full" data-testid="all-cheques-table">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Customer</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Cheque #</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Returned To</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Return Date</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Cheque Amt</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Received</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Method</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filteredCheques.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-16">\n                    <div className="flex flex-col items-center gap-4">
                      <div className="p-4 bg-slate-800/30 rounded-full">
                        <Search className="w-10 h-10 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-slate-400 font-medium text-lg">No cheque returns found</p>
                        <p className="text-sm text-slate-600 mt-1">Try adjusting your filters or add new entries</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCheques.map((cheque, index) => (
                  <tr 
                    key={cheque.id} 
                    className="hover:bg-slate-800/30 transition-colors" 
                    data-testid={`report-row-${cheque.id}`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <td className="py-4 px-4 text-slate-200 font-medium">{cheque.customer_name}</td>
                    <td className="py-4 px-4 text-slate-400 font-mono text-sm">{cheque.cheque_number}</td>
                    <td className="py-4 px-4 text-slate-300">{cheque.returned_to}</td>
                    <td className="py-4 px-4 text-slate-400 text-sm">{cheque.cheque_return_date}</td>
                    <td className="py-4 px-4 text-slate-300 font-semibold">₹{cheque.cheque_amount.toLocaleString()}</td>
                    <td className="py-4 px-4 text-emerald-400 font-semibold">₹{cheque.amount_received.toLocaleString()}</td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        cheque.payment_method === 'Cash' 
                          ? 'bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/20' 
                          : 'bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/20'
                      }`}>
                        {cheque.payment_method}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(cheque)}
                          data-testid={`edit-btn-${cheque.id}`}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-emerald-400"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(cheque.id)}
                          data-testid={`delete-btn-${cheque.id}`}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingCheque && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="edit-modal">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-slate-50">Edit Cheque Return</h2>
            <button
              onClick={() => setShowEditModal(false)}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                Customer Name
              </label>
              <input
                type="text"
                name="customer_name"
                value={editingCheque.customer_name}
                onChange={handleEditChange}
                required
                className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Cheque Number
                </label>
                <input
                  type="text"
                  name="cheque_number"
                  value={editingCheque.cheque_number}
                  onChange={handleEditChange}
                  required
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Payment Method
                </label>
                <select
                  name="payment_method"
                  value={editingCheque.payment_method}
                  onChange={handleEditChange}
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                >
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Cheque Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    name="cheque_amount"
                    value={editingCheque.cheque_amount}
                    onChange={handleEditChange}
                    required
                    className="w-full pl-8 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Amount Received
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    name="amount_received"
                    value={editingCheque.amount_received}
                    onChange={handleEditChange}
                    required
                    className="w-full pl-8 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Payment Date
                </label>
                <input
                  type="date"
                  name="payment_date"
                  value={editingCheque.payment_date}
                  onChange={handleEditChange}
                  required
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Cheque Return Date
                </label>
                <input
                  type="date"
                  name="cheque_return_date"
                  value={editingCheque.cheque_return_date}
                  onChange={handleEditChange}
                  required
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                Returned To Whom
              </label>
              <input
                type="text"
                name="returned_to"
                value={editingCheque.returned_to}
                onChange={handleEditChange}
                required
                className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/30"
              >
                {loading ? "Updating..." : "Update Cheque"}
              </button>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Delete Confirmation Modal */}
    {deleteConfirm && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="delete-modal">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 max-w-md w-full">
          <h2 className="text-xl font-semibold text-slate-50 mb-4">Delete Cheque Return?</h2>
          <p className="text-slate-400 mb-6">Are you sure you want to delete this cheque return? This action cannot be undone.</p>
          <div className="flex gap-3">
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-red-900/30"
            >
              Delete
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default ReportsView;